/**
 * GlobalConfig Hooks - Extended with siteId lookup
 *
 * @override
 * - Re-exports all base global-config hooks from @amblique/contentful
 * - Adds useGlobalConfigBySiteId hook for fetching GlobalConfig by site identifier (explicit siteId)
 * - Adds useGlobalConfigBySite hook for fetching GlobalConfig by current siteId from MultiSiteProvider (auto-detect)
 */

import {print} from 'graphql';

import {useQuery} from '@tanstack/react-query';

import {useService} from '@amblique/base/app/hooks/use-service';
import {ContentfulService} from '@amblique/contentful/app/services/contentful-service';
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site';
import {
  getBaseGlobalConfigBySiteId,
  getGlobalConfigBySiteId,
  getFullGlobalConfigBySiteId,
} from '@amblique/king-living/app/contentful/queries/global-config.queries';

// Re-export all base hooks
export * from '@amblique/contentful/app/contentful/hooks/use-global-config';

/**
 * Hook for fetching a GlobalConfig by its siteId field
 *
 * @param {string} siteId - The site identifier (e.g., 'kl-au', 'kl-nz')
 * @param {Object} [options={}] - Hook options
 * @param {'base'|'default'|'full'} [options.variant='default'] - Query variant (base: scalars only, default: 1-level deep, full: 2-levels deep)
 * @param {Object} [options.variables={}] - Additional GraphQL query variables
 * @param {import('@tanstack/react-query').UseQueryOptions<Contentful.GlobalConfig|null, Error>} [options.queryOptions={}] - React Query options
 * @param {RequestInit} [options.requestOptions={}] - HTTP request options (headers, cache, etc.)
 * @returns {import('@tanstack/react-query').UseQueryResult<Contentful.GlobalConfig|null, Error>}
 *
 * @example
 * // Basic usage (default variant)
 * const { data: globalConfig, isLoading } = useGlobalConfigBySiteId('kl-au');
 *
 * @example
 * // With variant selection
 * const { data } = useGlobalConfigBySiteId('kl-nz', {
 *   variant: 'full',  // Get complete nested data
 *   variables: { preview: true }
 * });
 *
 * @example
 * // With all options
 * const { data } = useGlobalConfigBySiteId('kl-au', {
 *   variant: 'base',  // Fast, scalars only
 *   variables: { preview: true },
 *   queryOptions: { staleTime: 0 },
 *   requestOptions: { cache: 'no-store' }
 * });
 */
export function useGlobalConfigBySiteId(siteId, options = {}) {
  const contentfulService = useService(ContentfulService);

  const {
    variant = 'default',
    variables = {},
    queryOptions = {},
    requestOptions = {},
  } = options;

  // Select query based on variant
  const query = variant === 'base' ?
    getBaseGlobalConfigBySiteId :
    variant === 'full' ?
      getFullGlobalConfigBySiteId :
      getGlobalConfigBySiteId;

  return useQuery({
    queryKey: [
      contentfulService?.key,
      'globalConfig',
      'siteId',
      siteId,
      variant,
      contentfulService?.locale,
      JSON.stringify(variables)
    ],

    queryFn: async () => {
      if (!contentfulService) {
        throw new Error('ContentfulService not available');
      }

      const data = await contentfulService.query(
        print(query),
        {
          siteId,
          locale: contentfulService?.locale,
          preview: false,
          ...variables,
        },
        requestOptions
      );

      return data.globalConfigCollection?.items?.[0] || null;
    },

    enabled: !!contentfulService && !!siteId,

    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
    refetchOnWindowFocus: false,

    retry: (failureCount, error) => {
      // Don't retry on 4xx errors
      if (error?.status >= 400 && error?.status < 500) {
        return false;
      }

      return failureCount < 3;
    },

    ...queryOptions,
  });
}

/**
 * Hook for fetching GlobalConfig by current siteId from MultiSiteProvider
 * Automatically detects siteId from MultiSiteProvider context
 *
 * @param {Object} [options={}] - Hook options
 * @param {'base'|'default'|'full'} [options.variant='default'] - Query variant (base: scalars only, default: 1-level deep, full: 2-levels deep)
 * @param {Object} [options.variables={}] - Additional GraphQL query variables
 * @param {import('@tanstack/react-query').UseQueryOptions<Contentful.GlobalConfig|null, Error>} [options.queryOptions={}] - React Query options
 * @param {RequestInit} [options.requestOptions={}] - HTTP request options (headers, cache, etc.)
 * @returns {import('@tanstack/react-query').UseQueryResult<Contentful.GlobalConfig|null, Error>}
 *
 * @example
 * // Basic usage (default variant)
 * const { data: globalConfig, isLoading } = useGlobalConfigBySite();
 *
 * @example
 * // With variant selection
 * const { data } = useGlobalConfigBySite({
 *   variant: 'full',  // Get complete nested data
 *   variables: { preview: true }
 * });
 *
 * @example
 * // With custom cache settings
 * const { data } = useGlobalConfigBySite({
 *   queryOptions: {
 *     staleTime: 30 * 60 * 1000, // 30 minutes
 *   }
 * });
 */
export function useGlobalConfigBySite(options = {variant: 'default'}) {
  const {site} = useMultiSite();
  const siteId = site?.id;


  // Delegate to useGlobalConfigBySiteId with auto-detected siteId
  const result = useGlobalConfigBySiteId(siteId, {
    ...options,
    queryOptions: {
      staleTime: 15 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
      ...options.queryOptions,
    },
  });

  return result;
}
